import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'

import { axiosInstance } from '../helpers'
import {
    BulkCreateRenewalOffersInput,
    BlockLeaseResponse,
    CreateNewLeaseOfferInput,
    NewLeaseOfferPreview,
    PreviewNewLeaseOfferInput,
    DeliveryLog,
    EligibleLease,
    OfferEnrollment,
    OfferUnitConfig,
    RenewalOffer,
    RenewalOfferStatus,
    UpdateRenewalOfferInput,
} from '../models/Concession'
import { useUser } from './useUser'

const OFFERS_KEY = 'renewal-offers'
const ELIGIBLE_KEY = 'eligible-leases'
const BLOCKED_KEY = 'blocked-leases'
const ENROLLMENT_KEY = 'offer-enrollment'
const LOGS_KEY = 'offer-delivery-logs'
const BASE = '/beta/api/concession/renewal-offers'

const extractError = (error: unknown, fallback: string): string => {
    const data = (error as AxiosError)?.response?.data as
        | Record<string, unknown>
        | undefined

    if (data) {
        const nonField = data.non_field_errors
        if (Array.isArray(nonField) && nonField.length > 0) {
            return String(nonField[0])
        }
        const firstKey = Object.keys(data)[0]
        if (firstKey) {
            const val = data[firstKey]
            if (Array.isArray(val) && val.length > 0) {
                return `${firstKey}: ${val[0]}`
            }
            if (typeof val === 'string') return val
        }
    }
    return fallback
}

export const useRenewalOffers = (options?: {
    statusFilter?: RenewalOfferStatus | ''
    leasingSeasonId?: number
    kind?: 'renewal' | 'new' | ''
}) => {
    const { statusFilter, leasingSeasonId, kind } = options ?? {}
    const queryClient = useQueryClient()
    const { workspaceUser } = useUser()

    const apartmentId = workspaceUser?.active_workspace?.id
    const isApartment =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'

    const { data: offers, isLoading, refetch } = useQuery<
        RenewalOffer[],
        Error
    >({
        queryKey: [
            OFFERS_KEY,
            apartmentId,
            statusFilter,
            leasingSeasonId,
            kind,
        ],
        queryFn: async () => {
            const params = new URLSearchParams()
            if (statusFilter) params.set('status', statusFilter)
            if (kind) params.set('kind', kind)
            if (leasingSeasonId)
                params.set('leasing_season', String(leasingSeasonId))
            const qs = params.toString() ? `?${params.toString()}` : ''
            const res = await axiosInstance.get<RenewalOffer[]>(`${BASE}/${qs}`)
            return res.data
        },
        enabled: isApartment && !!apartmentId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: [OFFERS_KEY] })
        queryClient.invalidateQueries({ queryKey: [ELIGIBLE_KEY] })
    }

    const bulkCreate = useMutation({
        mutationFn: async (body: BulkCreateRenewalOffersInput) => {
            const res = await axiosInstance.post<RenewalOffer[]>(
                `${BASE}/bulk-create/`,
                body,
            )
            return res.data
        },
        onSuccess: (data) => {
            invalidate()
            toast.success(
                `Created ${data.length} draft offer${
                    data.length === 1 ? '' : 's'
                }`,
            )
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to create offers'))
        },
    })

    const updateOffer = useMutation({
        mutationFn: async ({
            id,
            body,
        }: {
            id: number
            body: UpdateRenewalOfferInput
        }) => {
            const res = await axiosInstance.patch<RenewalOffer>(
                `${BASE}/${id}/`,
                body,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Offer updated')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to update offer'))
        },
    })

    const deleteOffer = useMutation({
        mutationFn: async (id: number) => {
            await axiosInstance.delete(`${BASE}/${id}/`)
        },
        onSuccess: () => {
            invalidate()
            toast.success('Offer deleted')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to delete offer'))
        },
    })

    const sendOffer = useMutation({
        mutationFn: async ({
            id,
            sequence,
        }: {
            id: number
            sequence?: number | null
        }) => {
            const body: Record<string, unknown> = {}
            if (sequence !== undefined) body.sequence = sequence
            const res = await axiosInstance.post<RenewalOffer>(
                `${BASE}/${id}/send/`,
                body,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Offer sent')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to send offer'))
        },
    })

    const bulkSend = useMutation({
        mutationFn: async ({
            offerIds,
            sequence,
        }: {
            offerIds: number[]
            sequence?: number | null
        }) => {
            const body: Record<string, unknown> = { offer_ids: offerIds }
            if (sequence !== undefined) body.sequence = sequence
            const res = await axiosInstance.post<RenewalOffer[]>(
                `${BASE}/bulk-send/`,
                body,
            )
            return res.data
        },
        onSuccess: (data) => {
            invalidate()
            toast.success(
                `Sent ${data.length} offer${data.length === 1 ? '' : 's'}`,
            )
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to send offers'))
        },
    })

    const revokeOffer = useMutation({
        mutationFn: async (id: number) => {
            const res = await axiosInstance.post<RenewalOffer>(
                `${BASE}/${id}/revoke/`,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Offer revoked')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to revoke offer'))
        },
    })

    const bulkRevoke = useMutation({
        mutationFn: async (offerIds: number[]) => {
            const res = await axiosInstance.post<RenewalOffer[]>(
                `${BASE}/bulk-revoke/`,
                { offer_ids: offerIds },
            )
            return res.data
        },
        onSuccess: (data) => {
            invalidate()
            toast.success(
                `Revoked ${data.length} offer${data.length === 1 ? '' : 's'}`,
            )
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to revoke offers'))
        },
    })

    const changeSequence = useMutation({
        mutationFn: async ({
            id,
            sequence,
        }: {
            id: number
            sequence: number | null
        }) => {
            const res = await axiosInstance.post<RenewalOffer>(
                `${BASE}/${id}/change-sequence/`,
                { sequence },
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            queryClient.invalidateQueries({ queryKey: [ENROLLMENT_KEY] })
            queryClient.invalidateQueries({ queryKey: [LOGS_KEY] })
            toast.success('Sequence updated')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to change sequence'))
        },
    })

    const blockLease = useMutation({
        mutationFn: async (leaseId: number) => {
            const res = await axiosInstance.post<BlockLeaseResponse>(
                `${BASE}/block-lease/`,
                { lease: leaseId },
            )
            return res.data
        },
        onSuccess: (data) => {
            invalidate()
            queryClient.invalidateQueries({ queryKey: [ELIGIBLE_KEY] })
            queryClient.invalidateQueries({ queryKey: [BLOCKED_KEY] })
            toast.success(
                data.renewal_blocked
                    ? 'Lease blocked from renewals'
                    : 'Lease unblocked for renewals',
            )
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to toggle lease block'))
        },
    })

    const sendLeasePackage = useMutation({
        mutationFn: async (id: number) => {
            const res = await axiosInstance.post<RenewalOffer>(
                `${BASE}/${id}/send-lease-package/`,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Lease packet sent')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to send lease packet'))
        },
    })

    const createNewLeaseOffer = useMutation({
        mutationFn: async (body: CreateNewLeaseOfferInput) => {
            const res = await axiosInstance.post<RenewalOffer>(
                `${BASE}/create-new-lease/`,
                body,
            )
            return res.data
        },
        onSuccess: (offer) => {
            invalidate()
            toast.success(
                offer.status === 'offered'
                    ? 'Lease offer sent to the applicant'
                    : 'Lease offer drafted',
            )
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to send lease offer'))
        },
    })

    return {
        offers: offers ?? [],
        isLoading,
        isApartment,
        refetch,
        bulkCreate,
        updateOffer,
        deleteOffer,
        sendOffer,
        bulkSend,
        revokeOffer,
        bulkRevoke,
        changeSequence,
        blockLease,
        sendLeasePackage,
        createNewLeaseOffer,
    }
}

export function useNewLeaseOfferPreview(
    enabled: boolean,
    body: PreviewNewLeaseOfferInput | null,
) {
    return useQuery<NewLeaseOfferPreview, Error>({
        queryKey: ['new-lease-offer-preview', body],
        queryFn: async () => {
            const res = await axiosInstance.post<NewLeaseOfferPreview>(
                `${BASE}/preview-new-lease/`,
                body,
            )
            return res.data
        },
        enabled: enabled && body != null,
    })
}

export const useEligibleLeases = (enabled: boolean) => {
    const { workspaceUser } = useUser()
    const apartmentId = workspaceUser?.active_workspace?.id
    const isApartment =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'

    const { data: eligibleLeases, isLoading, refetch } = useQuery<
        EligibleLease[],
        Error
    >({
        queryKey: [ELIGIBLE_KEY, apartmentId],
        queryFn: async () => {
            const res = await axiosInstance.get<EligibleLease[]>(
                `${BASE}/eligible-leases/`,
            )
            return res.data
        },
        enabled: enabled && isApartment && !!apartmentId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    return {
        eligibleLeases: eligibleLeases ?? [],
        isLoading,
        refetch,
    }
}

export const useBlockedLeases = (enabled: boolean) => {
    const { workspaceUser } = useUser()
    const queryClient = useQueryClient()
    const apartmentId = workspaceUser?.active_workspace?.id
    const isApartment =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'

    const { data: blockedLeases, isLoading, refetch } = useQuery<
        EligibleLease[],
        Error
    >({
        queryKey: [BLOCKED_KEY, apartmentId],
        queryFn: async () => {
            const res = await axiosInstance.get<EligibleLease[]>(
                `${BASE}/eligible-leases/?renewal_blocked=true`,
            )
            return res.data
        },
        enabled: enabled && isApartment && !!apartmentId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const unblockLease = useMutation({
        mutationFn: async (leaseId: number) => {
            const res = await axiosInstance.post<{
                lease: number
                renewal_blocked: boolean
            }>(`${BASE}/block-lease/`, { lease: leaseId })
            return res.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [BLOCKED_KEY] })
            queryClient.invalidateQueries({ queryKey: [ELIGIBLE_KEY] })
            toast.success('Tenant unblocked for renewals')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to unblock tenant'))
        },
    })

    return {
        blockedLeases: blockedLeases ?? [],
        isLoading,
        refetch,
        unblockLease,
    }
}

export const useOfferEnrollment = (offerId?: number) => {
    const { data, isLoading, refetch } = useQuery<
        OfferEnrollment | null,
        Error
    >({
        queryKey: [ENROLLMENT_KEY, offerId],
        queryFn: async () => {
            try {
                const res = await axiosInstance.get<OfferEnrollment>(
                    `${BASE}/${offerId}/enrollment/`,
                )
                return res.data
            } catch (err) {
                const status = (err as AxiosError)?.response?.status
                if (status === 404) return null
                throw err
            }
        },
        enabled: !!offerId,
        refetchOnWindowFocus: false,
    })

    return { enrollment: data ?? null, isLoading, refetch }
}

export const useOfferDeliveryLogs = (offerId?: number) => {
    const { data, isLoading, refetch } = useQuery<DeliveryLog[], Error>({
        queryKey: [LOGS_KEY, offerId],
        queryFn: async () => {
            const res = await axiosInstance.get<DeliveryLog[]>(
                `${BASE}/${offerId}/delivery-logs/`,
            )
            return res.data
        },
        enabled: !!offerId,
        refetchOnWindowFocus: false,
    })

    return { logs: data ?? [], isLoading, refetch }
}

// ---------------------------------------------------------------------------
// Occupancy
// ---------------------------------------------------------------------------

import {
    OccupancyLease,
    OccupancyUnit,
} from '../containers/LeasingSeason/Occupancy/types'

export const useOccupancy = () => {
    const { data, isLoading, refetch } = useQuery<OccupancyLease[], Error>({
        queryKey: ['occupancy-leases'],
        queryFn: async () => {
            const res = await axiosInstance.get<OccupancyLease[]>(
                `${BASE}/occupancy/`,
            )
            return res.data
        },
        refetchOnWindowFocus: false,
    })

    return { leases: data ?? [], isLoading, refetch }
}

export const useOccupancySuggest = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (body?: { pm_weights?: Record<string, string> }) => {
            const res = await axiosInstance.post(
                `${BASE}/occupancy/suggest/`,
                body ?? {},
            )
            return res.data as {
                suggested_lease_ids: number[]
                unplaced_lease_ids: number[]
                suggested_count: number
                unplaced_count: number
            }
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['occupancy-leases'] })
            toast.success(
                `Suggested ${data.suggested_count} placement${
                    data.suggested_count === 1 ? '' : 's'
                }`,
            )
            if (data.unplaced_count) {
                toast.info(
                    `${data.unplaced_count} eligible resident${
                        data.unplaced_count === 1 ? '' : 's'
                    } could not be placed`,
                )
            }
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to suggest placements'))
        },
    })
}

export const useOccupancyClearSuggestions = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (leaseIds?: number[]) => {
            const res = await axiosInstance.post(
                `${BASE}/occupancy/clear-suggestions/`,
                { lease_ids: leaseIds },
            )
            return res.data as { cleared_count: number }
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['occupancy-leases'] })
            toast.success(
                `Cleared ${data.cleared_count} suggestion${
                    data.cleared_count === 1 ? '' : 's'
                }`,
            )
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to clear suggestions'))
        },
    })
}

export const useOccupancyConfirm = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (leaseIds?: number[]) => {
            const body = leaseIds == null ? {} : { lease_ids: leaseIds }
            const res = await axiosInstance.post(
                `${BASE}/occupancy/confirm/`,
                body,
            )
            return res.data as {
                confirmed_count: number
                assignments_sent?: number
                released_units?: Array<{
                    unit_name: string
                    sent_count: number
                }>
            }
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['occupancy-leases'] })
            const sent = data.assignments_sent ?? 0
            const units = (data.released_units || [])
                .map((row) => row.unit_name)
                .filter(Boolean)
            const unitLabel = units.length ? ` for ${units.join(', ')}` : ''
            toast.success(
                sent > 0
                    ? `Confirmed ${
                          data.confirmed_count
                      } · sent ${sent} assignment letter${
                          sent === 1 ? '' : 's'
                      }${unitLabel}`
                    : `Confirmed ${data.confirmed_count} placement${
                          data.confirmed_count === 1 ? '' : 's'
                      }`,
            )
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to confirm placements'))
        },
    })
}

export const useOccupancyAssign = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (body: { lease: number; area: number | null }) => {
            const res = await axiosInstance.post(
                `${BASE}/occupancy/assign/`,
                body,
            )
            return res.data as OccupancyLease
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['occupancy-leases'] })
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to move placement'))
        },
    })
}

export const useOccupancyUnits = (enabled = true) => {
    const { data, isLoading } = useQuery<OccupancyUnit[], Error>({
        queryKey: ['occupancy-units'],
        queryFn: async () => {
            const res = await axiosInstance.get<OccupancyUnit[]>(
                `${BASE}/units/`,
            )
            return res.data
        },
        enabled,
        refetchOnWindowFocus: false,
    })

    return { units: data ?? [], isLoading }
}

const EMPTY_OFFER_UNIT_CONFIGS: OfferUnitConfig[] = []

export const useOfferUnitConfigs = (
    enabled = true,
    leasingSeasonId?: number,
) => {
    const { data, isLoading } = useQuery<OfferUnitConfig[], Error>({
        queryKey: ['lease-offer-unit-configs', leasingSeasonId ?? null],
        queryFn: async () => {
            const res = await axiosInstance.get<OfferUnitConfig[]>(
                `${BASE}/unit-configs/`,
                {
                    params:
                        leasingSeasonId != null
                            ? { leasing_season: leasingSeasonId }
                            : undefined,
                },
            )
            return res.data
        },
        enabled,
        refetchOnWindowFocus: false,
    })

    return { unitConfigs: data ?? EMPTY_OFFER_UNIT_CONFIGS, isLoading }
}
